import * as React from "react";
import { useAuth } from "@/hooks/auth/use-auth";

// ---------------------------------------------------------------------------
// Types and Interfaces
// ---------------------------------------------------------------------------

type StorageType = "localStorage" | "sessionStorage";

interface MigrationConfig {
  from: {
    key: string;
    storage: StorageType;
    organizationScoped?: boolean; // Whether the source key is organization-scoped
  };
  cleanup?: boolean; // Whether to delete source after migration (default: true)
}

export interface BrowserStorageOptions<T> {
  storage: StorageType;
  organizationScoped?: boolean; // Whether to automatically scope with organization ID (default: true)
  migration?: MigrationConfig;
  serializer?: (value: T) => string;
  parser?: (value: string) => T;
  logger?: (error: Error | unknown) => void;
  syncData?: boolean; // Whether to sync changes across components (default: true)
}

// Custom event names for storage changes
const STORAGE_CHANGE_EVENT = "browser-storage-change";

interface StorageChangeEvent extends CustomEvent {
  detail: {
    key: string;
    storageType: StorageType;
    newValue: string | null;
    oldValue: string | null;
  };
}

// ---------------------------------------------------------------------------
// Organization ID Management (Shared with existing hooks)
// ---------------------------------------------------------------------------

let resolveOrgId: (() => void) | null = null;
const waitForOrgId = new Promise<void>((resolve) => {
  resolveOrgId = resolve;
});

// ---------------------------------------------------------------------------
// Core Migration Engine
// ---------------------------------------------------------------------------

/**
 * Migrates data between different browser storage types and key formats.
 * Supports all migration scenarios: localStorage ↔ sessionStorage with optional cleanup.
 */
function migrateBrowserStorageData<T>(
  sourceKey: string,
  sourceStorage: Storage,
  sourceOrganizationScoped: boolean,
  targetKey: string,
  targetStorage: Storage,
  organizationId: string,
  defaultValue: T,
  cleanup: boolean,
  parser: (value: string) => T,
  serializer: (value: T) => string,
  logger: (error: Error | unknown) => void,
): T {
  try {
    // Check if target already has data - if so, just clean up legacy if needed
    const existingData = targetStorage.getItem(targetKey);
    if (existingData !== null) {
      // Handle corrupted values
      if (existingData === "undefined" || existingData === "null") {
        targetStorage.removeItem(targetKey);
        // Fall through to try migration from source
      } else {
        // Clean up legacy source if it exists and cleanup is enabled
        if (cleanup) {
          const isAlreadyScoped =
            sourceKey.startsWith("org_") && sourceKey.includes("::");
          const effectiveSourceKey =
            sourceOrganizationScoped && !isAlreadyScoped
              ? `${organizationId}::${sourceKey}`
              : sourceKey;

          if (sourceStorage.getItem(effectiveSourceKey) !== null) {
            sourceStorage.removeItem(effectiveSourceKey);
          }
        }

        return parser(existingData);
      }
    }

    // Build source key with proper scoping
    const isAlreadyScoped =
      sourceKey.startsWith("org_") && sourceKey.includes("::");
    const effectiveSourceKey =
      sourceOrganizationScoped && !isAlreadyScoped
        ? `${organizationId}::${sourceKey}`
        : sourceKey;

    // Try to get data from legacy source
    const sourceData = sourceStorage.getItem(effectiveSourceKey);
    if (sourceData !== null) {
      // Handle corrupted values
      if (sourceData === "undefined" || sourceData === "null") {
        if (cleanup) {
          sourceStorage.removeItem(effectiveSourceKey);
        }
        return defaultValue;
      }

      const parsedData = parser(sourceData);

      // Migrate to target
      targetStorage.setItem(targetKey, serializer(parsedData));

      // Cleanup source if requested
      if (cleanup) {
        sourceStorage.removeItem(effectiveSourceKey);
      }

      return parsedData;
    }
  } catch (error) {
    logger(`Migration error from ${sourceKey} to ${targetKey}: ${error}`);
  }

  return defaultValue;
}

// ---------------------------------------------------------------------------
// Storage Utilities
// ---------------------------------------------------------------------------

function getStorage(type: StorageType): Storage {
  if (typeof window === "undefined") {
    // SSR fallback - return a mock storage that does nothing
    return {
      getItem: () => null,
      setItem: () => {},
      removeItem: () => {},
      clear: () => {},
      length: 0,
      key: () => null,
    };
  }

  return type === "localStorage" ? window.localStorage : window.sessionStorage;
}

function dispatchStorageChangeEvent(
  key: string,
  storageType: StorageType,
  newValue: string | null,
  oldValue: string | null,
) {
  if (typeof window === "undefined") return;

  // Use setTimeout to ensure proper event ordering
  setTimeout(() => {
    const event = new CustomEvent(STORAGE_CHANGE_EVENT, {
      detail: { key, storageType, newValue, oldValue },
    }) as StorageChangeEvent;

    window.dispatchEvent(event);
  }, 0);
}

// ---------------------------------------------------------------------------
// Main Hook
// ---------------------------------------------------------------------------

/**
 * Unified browser storage hook that supports both localStorage and sessionStorage
 * with powerful migration capabilities between any storage types.
 *
 * @param key - The storage key (will be automatically scoped to organization if organizationScoped=true)
 * @param defaultValue - Default value if nothing is stored
 * @param options - Configuration options including storage type and migration settings
 * @returns [value, setValue] tuple similar to useState
 *
 * @example
 * // Basic localStorage usage
 * const [data, setData] = useBrowserStorage('my-key', {}, { storage: 'localStorage' });
 *
 * @example
 * // Migrate from localStorage to sessionStorage
 * const [filters, setFilters] = useBrowserStorage('filters::v2', {}, {
 *   storage: 'sessionStorage',
 *   migration: {
 *     from: { key: 'filters_v1', storage: 'localStorage' },
 *     cleanup: true
 *   }
 * });
 *
 * @example
 * // Migrate between key formats in same storage
 * const [settings, setSettings] = useBrowserStorage('new-settings-key', {}, {
 *   storage: 'localStorage',
 *   migration: {
 *     from: { key: 'old-settings-key', storage: 'localStorage' },
 *     cleanup: true
 *   }
 * });
 */
export function useBrowserStorage<T>(
  key: string,
  defaultValue: T,
  options: BrowserStorageOptions<T>,
): [T, React.Dispatch<React.SetStateAction<T>>] {
  // Destructure options with defaults
  const {
    storage,
    organizationScoped = true,
    migration,
    serializer = JSON.stringify,
    parser = JSON.parse,
    logger = console.error,
    syncData = true,
  } = options;

  const { user } = useAuth();
  const organizationId = user?.company?.default_organization?.id;

  // Suspend until organization context is ready (only if organization scoping is enabled)
  if (organizationScoped && !organizationId) {
    throw waitForOrgId;
  }

  // Resolve suspension promise when org ID becomes available
  if (organizationScoped && organizationId && resolveOrgId) {
    resolveOrgId();
    resolveOrgId = null;
  }

  // Build the effective storage key
  const effectiveKey =
    organizationScoped && organizationId ? `${organizationId}::${key}` : key;

  // Get the appropriate storage instance
  const storageInstance = React.useMemo(() => getStorage(storage), [storage]);

  // Track raw value for change detection
  const rawValueRef = React.useRef<string | null>(null);

  // Initialize state with migration support
  const [value, setValue] = React.useState<T>(() => {
    if (typeof window === "undefined") return defaultValue;

    // Handle migration if configured
    if (migration && organizationId) {
      return migrateBrowserStorageData(
        migration.from.key,
        getStorage(migration.from.storage),
        migration.from.organizationScoped ?? true,
        effectiveKey,
        storageInstance,
        organizationId,
        defaultValue,
        migration.cleanup ?? true,
        parser,
        serializer,
        logger,
      );
    }

    // Standard initialization without migration
    try {
      const item = storageInstance.getItem(effectiveKey);
      rawValueRef.current = item;

      // Handle corrupted or invalid values (like the string "undefined")
      if (item === "undefined" || item === "null") {
        storageInstance.removeItem(effectiveKey);
        rawValueRef.current = null;
        return defaultValue;
      }

      return item ? parser(item) : defaultValue;
    } catch (error) {
      logger(`Error reading from ${storage} for key ${effectiveKey}: ${error}`);
      // Clean up corrupted value
      try {
        storageInstance.removeItem(effectiveKey);
        rawValueRef.current = null;
      } catch {
        // Ignore cleanup errors
      }
      return defaultValue;
    }
  });

  // Update storage when value changes
  React.useEffect(() => {
    if (typeof window === "undefined") return;

    try {
      // Handle undefined values by removing the item from storage
      if (value === undefined) {
        const oldValue = rawValueRef.current;
        if (oldValue !== null) {
          rawValueRef.current = null;
          storageInstance.removeItem(effectiveKey);

          // Dispatch change event for cross-component sync
          if (syncData) {
            dispatchStorageChangeEvent(effectiveKey, storage, null, oldValue);
          }
        }
        return;
      }

      const newValue = serializer(value);
      const oldValue = rawValueRef.current;

      // Only update if value actually changed
      if (newValue !== oldValue) {
        rawValueRef.current = newValue;
        storageInstance.setItem(effectiveKey, newValue);

        // Dispatch change event for cross-component sync
        if (syncData) {
          dispatchStorageChangeEvent(effectiveKey, storage, newValue, oldValue);
        }
      }
    } catch (error) {
      logger(`Error writing to ${storage} for key ${effectiveKey}: ${error}`);
    }
  }, [
    effectiveKey,
    storage,
    value,
    serializer,
    storageInstance,
    syncData,
    logger,
  ]);

  // Listen for storage changes from other components
  React.useEffect(() => {
    if (!syncData || typeof window === "undefined") return;

    const handleStorageChange = (event: Event) => {
      const storageEvent = event as StorageChangeEvent;
      const { key: eventKey, storageType, newValue } = storageEvent.detail;

      // Only respond to changes for our specific key and storage type
      if (eventKey !== effectiveKey || storageType !== storage) return;

      try {
        if (newValue !== rawValueRef.current) {
          rawValueRef.current = newValue;
          const parsedValue = newValue ? parser(newValue) : defaultValue;

          // Use startTransition for non-blocking updates
          React.startTransition(() => {
            setValue((prevValue) => {
              // Deep comparison for objects/arrays to avoid unnecessary re-renders
              if (JSON.stringify(prevValue) === JSON.stringify(parsedValue)) {
                return prevValue; // No change, return same reference
              }
              return parsedValue;
            });
          });
        }
      } catch (error) {
        logger(
          `Error parsing ${storage} change event for key ${effectiveKey}: ${error}`,
        );
      }
    };

    // Listen for our custom storage change events
    window.addEventListener(STORAGE_CHANGE_EVENT, handleStorageChange);

    // For localStorage, also listen for native storage events (cross-tab sync)
    if (storage === "localStorage") {
      const handleNativeStorageChange = (event: StorageEvent) => {
        if (
          event.key !== effectiveKey ||
          event.storageArea !== window.localStorage
        )
          return;

        try {
          if (event.newValue !== rawValueRef.current) {
            rawValueRef.current = event.newValue;
            React.startTransition(() => {
              setValue(event.newValue ? parser(event.newValue) : defaultValue);
            });
          }
        } catch (error) {
          logger(
            `Error parsing native storage event for key ${effectiveKey}: ${error}`,
          );
        }
      };

      window.addEventListener("storage", handleNativeStorageChange);

      return () => {
        window.removeEventListener(STORAGE_CHANGE_EVENT, handleStorageChange);
        window.removeEventListener("storage", handleNativeStorageChange);
      };
    }

    return () => {
      window.removeEventListener(STORAGE_CHANGE_EVENT, handleStorageChange);
    };
  }, [effectiveKey, storage, syncData, parser, defaultValue, logger]);

  return [value, setValue];
}

// ---------------------------------------------------------------------------
// Convenience Hooks (Backward Compatibility)
// ---------------------------------------------------------------------------

/**
 * Convenience hook for localStorage with organization scoping and migration support.
 * Equivalent to useBrowserStorage with storage: 'localStorage'.
 */
export function useOrganizationLocalStorage<T>(
  key: string,
  defaultValue: T,
  options?: Omit<BrowserStorageOptions<T>, "storage"> & {
    legacyKey?: string; // Backward compatibility with existing API
  },
) {
  const migrationConfig = options?.legacyKey
    ? {
        from: {
          key: options.legacyKey,
          storage: "localStorage" as StorageType,
          organizationScoped: false, // Legacy keys were often unscoped
        },
        cleanup: true,
      }
    : options?.migration;

  return useBrowserStorage(key, defaultValue, {
    ...options,
    storage: "localStorage",
    migration: migrationConfig,
  });
}

/**
 * Convenience hook for sessionStorage with organization scoping.
 * Equivalent to useBrowserStorage with storage: 'sessionStorage'.
 */
export function useOrganizationSessionStorage<T>(
  key: string,
  defaultValue: T,
  options?: Omit<BrowserStorageOptions<T>, "storage">,
) {
  return useBrowserStorage(key, defaultValue, {
    ...options,
    storage: "sessionStorage",
  });
}

// ---------------------------------------------------------------------------
// Legacy Hook (Simple localStorage without organization scoping)
// ---------------------------------------------------------------------------

/**
 * Simple localStorage hook without organization scoping.
 * Kept for backward compatibility where organization scoping is not needed.
 */
export function useLocalStorage<T>(
  key: string,
  defaultValue: T,
  options?: Omit<BrowserStorageOptions<T>, "storage" | "organizationScoped">,
) {
  return useBrowserStorage(key, defaultValue, {
    ...options,
    storage: "localStorage",
    organizationScoped: false,
  });
}

// ---------------------------------------------------------------------------
// Search State Hook (Built on sessionStorage)
// ---------------------------------------------------------------------------

/**
 * Hook specifically for search state that automatically manages
 * search query, input value, and debounced search with sessionStorage.
 *
 * @param key - The storage key for this search instance
 * @param debounceMs - Debounce delay in milliseconds (default: 300)
 * @returns Object with search state and handlers
 */
// Deprecated implementation: prefer hooks/use-search-state
// Keep a thin wrapper for backward compatibility where consumers expect an event handler signature
export { useSearchState } from "./use-search-state";
